import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { insightsApi } from '../../api/insights';

interface ExplainPanelProps {
  view: string;
  entityId?: number;
  title?: string;
}

/**
 * "Paaiškinti" narration. Facts are gathered server-side (bridge → .NET);
 * Mistral only phrases them, and the bridge strips any number that does not
 * exist verbatim in the facts — the panel can therefore render the text as-is.
 * Rendered only where AI egress is enabled (the page gates it).
 */
export default function ExplainPanel({ view, entityId, title }: ExplainPanelProps) {
  const { t } = useTranslation();
  const [text, setText] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');

  const run = async () => {
    setBusy(true);
    setError('');
    try {
      const result = await insightsApi.explain(view, entityId);
      setText(result.text);
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(false);
    }
  };

  if (text !== null) {
    return (
      <div className="bg-primary-50/60 border border-primary-100 rounded-lg p-4">
        <div className="flex items-start justify-between gap-4">
          <div>
            <div className="text-xs font-semibold text-primary-700 uppercase tracking-wide mb-1">
              {t('analytics.explain.title')}{title ? ` — ${title}` : ''}
            </div>
            <p className="text-sm text-gray-800 whitespace-pre-line">{text}</p>
          </div>
          <button onClick={() => setText(null)} className="text-gray-400 hover:text-gray-600 text-lg leading-none" title={t('common.close')}>×</button>
        </div>
      </div>
    );
  }

  return (
    <div>
      <button
        onClick={run}
        disabled={busy}
        className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm text-primary-700 bg-primary-50 border border-primary-200 rounded-full hover:border-primary-400 disabled:opacity-50 transition-colors"
      >
        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z" />
        </svg>
        {busy ? t('analytics.explain.loading') : t('analytics.explain.button')}
      </button>
      {error && <p className="mt-1 text-xs text-red-500">{error}</p>}
    </div>
  );
}
